import { createSelector } from '@reduxjs/toolkit';
import { isEmpty } from '@sgme/fp';

import { SavedCashForward } from '@/domain/WorkspaceForex/savedProducts/cash/SavedCashForward.ts';
import { SavedCashNdf } from '@/domain/WorkspaceForex/savedProducts/cash/SavedCashNdf.ts';
import { SavedCashSpot } from '@/domain/WorkspaceForex/savedProducts/cash/SavedCashSpot.ts';
import { SavedFlexiForward } from '@/domain/WorkspaceForex/savedProducts/flexi-forward/SavedFlexiForward';
import { SavedForwardAccumulator } from '@/domain/WorkspaceForex/savedProducts/forwardAccumulator/SavedForwardAccumulator.ts';
import { SavedOption } from '@/domain/WorkspaceForex/savedProducts/option/SavedOption.ts';
import { SavedCallOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedCallOrder.ts';
import { SavedFalconOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedFalconOrder.ts';
import { SavedFixingOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedFixingOrder.ts';
import { SavedNightjarOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedNightjarOrder.ts';
import { SavedStopLossOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedStopLossOrder.ts';
import { SavedTakeProfitOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedTakeProfitOrder.ts';
import { SavedTwapOrder } from '@/domain/WorkspaceForex/savedProducts/order/SavedTwapOrder.ts';
import { SavedSwapNds } from '@/domain/WorkspaceForex/savedProducts/swap/SavedSwapNds.ts';
import { SavedSwapRegular } from '@/domain/WorkspaceForex/savedProducts/swap/SavedSwapRegular.ts';
import { SavedTargetAccumulator } from '@/domain/WorkspaceForex/savedProducts/targetAccumulator/SavedTargetAccumulator.ts';
import { isTileSupportedInWorkspace } from '@/domain/WorkspaceForex/SavedTile.ts';
import { isOrderType } from '@/store/orders/helpers.ts';
import type { AppState } from '@/store/store.ts';

import { selectGetWorkspace, selectPinnedCompaniesQueryResult } from '.';
import type { Workspace } from './model.ts';

export const selectPinnedCompanies = createSelector(
  selectPinnedCompaniesQueryResult(),
  (result) => result.data?.pinnedCompanies ?? [],
);

export const selectWorkspaceBlotterColumnState = (blotter: 'cashTab' | 'optionTab' | 'orderTab' | 'accumulatorTab') =>
  createSelector(selectGetWorkspace(), (workspace) => workspace.data?.blotter?.[blotter].columnState);

/**
 * get from the state all datas representing the user workspace
 */
export const selectWorkspaceToSave = (state: AppState): Workspace => {
  const tabsToSave = Object.values(state.tabs.tabs.entities)
    // do not save shared tab in user personal workspace
    .filter((tab) => isEmpty(tab.sharedWithUsers))
    // return tab object with only the fields we want to persist
    .map((tab) => {
      if (tab.tabType !== 'tiles') {
        return tab;
      }
      const tabToSave = Object.assign({}, tab);
      delete tabToSave.zoomedTileId;
      return tabToSave;
    });

  const tilesToSave = Object.values(state.tiles.entities)
    .filter(isTileSupportedInWorkspace)
    .filter((tile) => tabsToSave.find((tab) => tab.id === tile.tabId))
    .filter(
      (tile) => !isOrderType(tile.productType) || state.orders?.[tile.tileId]?.properties.status.type === 'creating',
    );

  return {
    version: 10,
    blotter: {
      // TODO : commonize
      panelHeight: state.blotter.panelHeight,
      panelIsOpen: state.blotter.panelIsOpen,
      activeTab: state.blotter.activeTab,
      cashTab: {
        autoColumState: { width: 100 },
        columnState: state.blotter.columnState.Cash.map((col) => ({
          colId: col.colId,
          hide: col.hide ?? false,
          width: col.width ?? undefined,
          sort: col.sort ?? undefined,
          sortIndex: col.sortIndex ?? undefined,
        })),
      },
      optionTab: {
        autoColumState: { width: 100 },
        columnState: state.blotter.columnState.Option.map((col) => ({
          colId: col.colId,
          hide: col.hide ?? false,
          width: col.width ?? undefined,
          sort: col.sort ?? undefined,
          sortIndex: col.sortIndex ?? undefined,
        })),
      },
      orderTab: {
        autoColumState: { width: 100 },
        columnState: state.blotter.columnState.Order.map((col) => ({
          colId: col.colId,
          hide: col.hide ?? false,
          width: col.width ?? undefined,
          sort: col.sort ?? undefined,
          sortIndex: col.sortIndex ?? undefined,
        })),
      },
      accumulatorTab: {
        autoColumState: { width: 100 },
        columnState: state.blotter.columnState.Accumulator.map((col) => ({
          colId: col.colId,
          hide: col.hide ?? false,
          width: col.width ?? undefined,
          sort: col.sort ?? undefined,
          sortIndex: col.sortIndex ?? undefined,
        })),
      },
    },
    canTrade: state.workspace.canTrade,
    tabs: tabsToSave,
    selectedTabId: state.tabs.activeTabId,
    tiles: tilesToSave,
    products: {
      order: Object.entries(state.orders)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .filter(([_tileId, order]) => order.properties.status.type === 'creating')
        .map(([tileId, order]) => {
          switch (order.type) {
            case 'StopLossOrder':
              return SavedStopLossOrder.fromFxStopLossOrder(order, tileId);
            case 'TakeProfitOrder':
              return SavedTakeProfitOrder.fromFxTakeProfitOrder(order, tileId);
            case 'FixingOrder':
              return SavedFixingOrder.fromFxFixingOrder(order, tileId);
            case 'Twap+Order':
              return SavedTwapOrder.fromFxTwapOrder(order, tileId);
            case 'FalconOrder':
              return SavedFalconOrder.fromFxFalconOrder(order, tileId);
            case 'NightjarOrder':
              return SavedNightjarOrder.fromFxNightjarOrder(order, tileId);
            case 'CallOrder':
              return SavedCallOrder.fromFxCallOrder(order, tileId);
            default:
              // unreachable, as far as order.type honors its typing
              // this makes both typescript and biome happy
              throw Error('Unexpected order type');
          }
        }),
      cash: Object.entries(state.cash)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .map(([tileId, cash]) => {
          switch (cash.type) {
            case 'FxFwd':
              return SavedCashForward.fromFxCashForward(cash, tileId);
            case 'FxNdf':
              return SavedCashNdf.fromFxCashNdf(cash, tileId);
            case 'FxSpot':
              return SavedCashSpot.fromFxCashSpot(cash, tileId);
            default:
              // unreachable, as far as cash.type honors its typing
              // this makes both typescript and biome happy
              throw Error('Unexpected cash type');
          }
        }),
      option: Object.entries(state.option)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .map(([tileId, option]) => SavedOption.fromOption(option, tileId)),
      targetAccumulator: Object.entries(state.targetAccumulator)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .map(([tileId, targetAccumulator]) => SavedTargetAccumulator.fromTargetAccumulator(targetAccumulator, tileId)),
      forwardAccumulator: Object.entries(state.forwardAccumulator)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .map(([tileId, forwardAccumulator]) =>
          SavedForwardAccumulator.fromForwardAccumulator(forwardAccumulator, tileId),
        ),
      swap: Object.entries(state.swap)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .map(([tileId, swap]) => {
          if (swap.isNonDeliverable) {
            return SavedSwapNds.fromFxSwapNds(swap, tileId);
          }
          return SavedSwapRegular.fromFxSwapRegular(swap, tileId);
        }),
      flexiForward: Object.entries(state.flexiForward)
        .filter(([tileId]) => tilesToSave.find((tile) => tile.tileId === tileId))
        .map(([tileId, flexiForward]) => SavedFlexiForward.fromFlexiForward(flexiForward, tileId)),
    },
  };
};
